import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getExams, getExamStats } from '../services/examService';
import { createTestExam } from '../services/testService';
import { PlusIcon, ChartBarIcon, ClockIcon, TrophyIcon, CalendarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import NetProgressChart from '../components/charts/NetProgressChart';
import Logo from '../components/Logo';
import axios from 'axios';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentExams, setRecentExams] = useState([]);
  const [allExams, setAllExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingTest, setCreatingTest] = useState(false);
  const [tytProgram, setTytProgram] = useState(null);
  const [aytProgram, setAytProgram] = useState(null);
  const [subjectViewType, setSubjectViewType] = useState('AYT'); // AYT default

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsResponse, examsResponse, allExamsResponse] = await Promise.all([
        getExamStats({ examType: 'all' }),
        getExams({ limit: 5 }),
        getExams() // Get all exams for charts
      ]);
      console.log('Stats Response:', statsResponse); // Debug
      console.log('Exams Response:', examsResponse); // Debug
      console.log('Current User:', user); // Debug user data
      setStats(statsResponse.stats);
      setRecentExams(examsResponse.exams);
      setAllExams(allExamsResponse.exams);

      // Fetch active program separately (don't block if it fails)
      await fetchActiveProgram(token);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveProgram = async (token) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await axios.get(`${API_URL}/study-programs/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setTytProgram(response.data.data.tyt);
        setAytProgram(response.data.data.ayt);
      }
    } catch (err) {
      // No active program is okay, just don't show the widget
      console.log('No active program found', err.response?.data || err.message);
      setTytProgram(null);
      setAytProgram(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCreateTestExam = async () => {
    if (creatingTest) return;
    
    setCreatingTest(true);
    try {
      const response = await createTestExam();
      console.log('Test denemeleri oluşturuldu:', response);
      
      // Verileri yenile
      await fetchData();
      
      // Success mesajı
      alert('🎉 Test denemeleri başarıyla oluşturuldu! (TYT + AYT)\n\n' + 
            '📘 TYT Denemesi:\n' +
            '✅ Türkçe (40 soru)\n' +
            '✅ Matematik (40 soru)\n' +
            '✅ Fen (20 soru)\n' +
            '✅ Sosyal (20 soru)\n\n' +
            '📗 AYT Denemesi:\n' +
            '✅ Matematik (40 soru)\n' +
            '✅ Fizik (14 soru)\n' +
            '✅ Kimya (13 soru)\n' +
            '✅ Biyoloji (13 soru)\n\n' +
            'Tüm konu detayları otomatik dolduruldu!');
      
      // İlk denemeye yönlendir (TYT)
      if (response.exams && response.exams.length > 0) {
        navigate(`/exam/${response.exams[0].examId}`);
      } else if (response.examId || response.exam?.id) {
        navigate(`/exam/${response.examId || response.exam.id}`);
      }
    } catch (error) {
      console.error('Test denemeleri oluşturma hatası:', error);
      alert('❌ Test denemeleri oluşturulurken hata oluştu:\n' + (error.response?.data?.message || error.message));
    } finally {
      setCreatingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile Responsive */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            {/* Logo */}
            <div className="flex items-center">
              <Logo showText={true} className="h-8 w-8 sm:h-10 sm:w-10" textClassName="text-xl sm:text-2xl font-bold text-gray-900" />
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-3">
              <span className="text-sm text-gray-600">
                Hoş geldin, <span className="font-semibold">{user?.name}</span>
              </span>
              {user?.role === 'admin' && (
                <Link
                  to="/admin"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition shadow-sm text-sm"
                >
                  🔐 Admin
                </Link>
              )}
              <Link
                to="/compare-exams"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition shadow-sm text-sm"
              >
                📊 Karşılaştır
              </Link>
              <Link
                to="/goals"
                className="px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition shadow-sm text-sm"
              >
                🎯 Hedef
              </Link>
              <Link
                to="/study-log"
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition shadow-sm text-sm"
              >
                📔 Defterim
              </Link>
              <Link
                to="/profile"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Profil
              </Link>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-900 font-medium"
              >
                Çıkış
              </button>
            </div>

            {/* Mobile Navigation - Compact */}
            <div className="flex lg:hidden items-center space-x-2">
              <Link
                to="/goals"
                className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
                title="Hedef"
              >
                <span className="text-sm">🎯</span>
              </Link>
              <Link
                to="/study-log"
                className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                title="Defterim"
              >
                <span className="text-sm">📔</span>
              </Link>
              <Link
                to="/profile"
                className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                title="Profil"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Motivasyon & Hedef - Mobile Responsive */}
        <div className="mb-6 sm:mb-8">
          {user?.targetGoal?.university && user?.targetGoal?.targetScore > 0 ? (
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl shadow-lg p-4 sm:p-6 text-white">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Sol Taraf - Hedef Bilgisi */}
                <div className="flex items-start sm:items-center space-x-3 sm:space-x-4 flex-1 min-w-0">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl sm:text-2xl">🎯</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xs sm:text-sm font-semibold uppercase tracking-wide opacity-90 mb-1">Hedef</h3>
                    <p className="text-base sm:text-lg font-bold truncate">{user.targetGoal.university} - {user.targetGoal.department}</p>
                    {user.targetGoal.scoreType && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded text-xs font-semibold">
                        {user.targetGoal.scoreType}
                      </span>
                    )}
                    <p className="text-xs sm:text-sm opacity-90 italic truncate mt-1">
                      "{user.targetGoal.motivationQuote || 'Hayallerimin peşinden koşuyorum! 🚀'}"
                    </p>
                  </div>
                </div>
                
                {/* Sağ Taraf - Stats */}
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 lg:gap-6">
                  {/* Puan Karşılaştırması */}
                  {stats?.averageScore && (
                    <div className="text-center bg-white/10 rounded-lg p-3 min-w-[100px]">
                      <div className="text-2xl sm:text-3xl font-bold">{stats.averageScore.toFixed(0)}</div>
                      <div className="text-xs opacity-90">Mevcut Ort.</div>
                      <div className={`text-xs sm:text-sm font-semibold mt-1 ${
                        stats.averageScore >= user.targetGoal.targetScore ? 'text-green-300' : 'text-yellow-300'
                      }`}>
                        {stats.averageScore >= user.targetGoal.targetScore 
                          ? '✅' 
                          : `→ ${user.targetGoal.targetScore.toFixed(0)}`
                        }
                      </div>
                    </div>
                  )}
                  
                  {/* TYT Net Hedefleri */}
                  {user?.targetNets && Object.values(user.targetNets.tyt).some(val => val > 0) && (
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3 min-w-[140px]">
                      <div className="text-xs opacity-80 mb-2 font-semibold text-center">📘 TYT</div>
                      <div className="grid grid-cols-2 gap-x-2 sm:gap-x-3 gap-y-1">
                        {user.targetNets.tyt.turkish > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Türkçe:</span>
                            <span className="font-bold">{user.targetNets.tyt.turkish}</span>
                          </div>
                        )}
                        {user.targetNets.tyt.math > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Mat:</span>
                            <span className="font-bold">{user.targetNets.tyt.math}</span>
                          </div>
                        )}
                        {user.targetNets.tyt.science > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Fen:</span>
                            <span className="font-bold">{user.targetNets.tyt.science}</span>
                          </div>
                        )}
                        {user.targetNets.tyt.social > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Sosyal:</span>
                            <span className="font-bold">{user.targetNets.tyt.social}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* AYT Net Hedefleri */}
                  {user?.targetNets && Object.values(user.targetNets.ayt).some(val => val > 0) && (
                    <div className="bg-white/10 rounded-lg p-2 sm:p-3 min-w-[140px]">
                      <div className="text-xs opacity-80 mb-2 font-semibold text-center">📕 AYT</div>
                      <div className="grid grid-cols-2 gap-x-2 sm:gap-x-3 gap-y-1">
                        {user.targetNets.ayt.mathAyt > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Mat:</span>
                            <span className="font-bold">{user.targetNets.ayt.mathAyt}</span>
                          </div>
                        )}
                        {user.targetNets.ayt.physics > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Fizik:</span>
                            <span className="font-bold">{user.targetNets.ayt.physics}</span>
                          </div>
                        )}
                        {user.targetNets.ayt.chemistry > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Kimya:</span>
                            <span className="font-bold">{user.targetNets.ayt.chemistry}</span>
                          </div>
                        )}
                        {user.targetNets.ayt.biology > 0 && (
                          <div className="text-xs flex justify-between items-center">
                            <span className="opacity-75">Biyoloji:</span>
                            <span className="font-bold">{user.targetNets.ayt.biology}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Taban-Tavan Puan Göstergesi */}
                  {user.targetGoal.baseScore && user.targetGoal.topScore && (
                    <div className="text-center bg-white/10 rounded-lg p-2 sm:p-3 min-w-[130px]">
                      <div className="text-xs opacity-80 mb-1">Puan Aralığı</div>
                      <div className="flex items-center gap-2">
                        <div className="text-center">
                          <div className="text-lg font-bold text-green-300">{user.targetGoal.topScore.toFixed(0)}</div>
                          <div className="text-xs opacity-75">Tavan</div>
                        </div>
                        <div className="text-white/50">-</div>
                        <div className="text-center">
                          <div className="text-lg font-bold text-orange-300">{user.targetGoal.baseScore.toFixed(0)}</div>
                          <div className="text-xs opacity-75">Taban</div>
                        </div>
                      </div>
                      {stats?.averageScore && (
                        <div className="mt-2 text-xs">
                          {stats.averageScore >= user.targetGoal.topScore ? (
                            <span className="text-green-300 font-semibold">🔥 Tavan puanın üstündesin!</span>
                          ) : stats.averageScore >= user.targetGoal.baseScore ? (
                            <span className="text-yellow-300 font-semibold">✨ Puan aralığındasın!</span>
                          ) : (
                            <span className="text-orange-200">📈 {(user.targetGoal.baseScore - stats.averageScore).toFixed(0)} puan kaldı</span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <Link
                    to="/goals"
                    className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition flex-shrink-0"
                    title="Hedefi Düzenle"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </Link>
                </div>
              </div>
              
              {stats?.averageScore && user.targetGoal.targetScore && (
                <div className="mt-4 pt-4 border-t border-white/30">
                  <div className="flex justify-between items-center text-xs sm:text-sm mb-2">
                    <span className="opacity-90">Hedefe İlerleme</span>
                    <span className="font-bold">
                      %{Math.min((stats.averageScore / user.targetGoal.targetScore) * 100, 100).toFixed(0)}
                    </span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-500 ${
                        (stats.averageScore / user.targetGoal.targetScore) * 100 >= 100
                          ? 'bg-green-400'
                          : (stats.averageScore / user.targetGoal.targetScore) * 100 >= 80
                          ? 'bg-yellow-400'
                          : 'bg-white'
                      }`}
                      style={{
                        width: `${Math.min((stats.averageScore / user.targetGoal.targetScore) * 100, 100).toFixed(1)}%`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl shadow-md p-4 sm:p-6 border-2 border-dashed border-purple-300">
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between gap-4">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 bg-purple-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-xl sm:text-2xl">🎯</span>
                  </div>
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900">Hedef Belirle!</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Hayalindeki üniversiteyi ve motivasyon sözünü ekle</p>
                  </div>
                </div>
                <Link
                  to="/goals"
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition flex items-center text-sm sm:text-base w-full sm:w-auto justify-center"
                >
                  <span className="mr-2">➕</span>
                  Hedef Ekle
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* AYT Journey Button */}
        <div className="mb-6 sm:mb-8">
          <Link
            to="/ayt-journey"
            className="block bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 rounded-xl shadow-lg p-6 text-white hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="h-14 w-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">🚀</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">AYT Yolculuğum</h3>
                  <p className="text-white/90 text-sm">Tüm konuları takip et, hedefine ulaş</p>
                </div>
              </div>
              <div className="hidden sm:flex items-center space-x-2 text-white/90">
                <span className="text-sm font-medium">Yolculuğa Başla</span>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </Link>
        </div>

        {/* Active Study Program Widget - Side by Side */}
        {/* Active Programs - TYT and AYT */}
        {(tytProgram || aytProgram) && (
          <div className="mb-6 sm:mb-8 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {tytProgram && (
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 sm:p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold">Aktif Çalışma Programın</h3>
                      <p className="text-sm opacity-90">TYT • Hafta {tytProgram.weekNumber}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Haftalık İlerleme</span>
                    <span className="text-sm font-semibold">
                      {tytProgram.completedTasks} / {tytProgram.totalTasks} Görev
                    </span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2.5">
                    <div
                      className="bg-white h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${tytProgram.completionPercentage || 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm mb-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>%{Math.round(tytProgram.completionPercentage || 0)} Tamamlandı</span>
                  </div>
                  <span className="opacity-90">
                    {tytProgram.totalTasks - tytProgram.completedTasks} görev kaldı
                  </span>
                </div>

                <Link
                  to={`/study-program?type=TYT`}
                  className="block w-full px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition text-center"
                >
                  Programa Git →
                </Link>
              </div>
            )}

            {aytProgram && (
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-4 sm:p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CalendarIcon className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-lg font-bold">Aktif Çalışma Programın</h3>
                      <p className="text-sm opacity-90">AYT • Hafta {aytProgram.weekNumber}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm opacity-90">Haftalık İlerleme</span>
                    <span className="text-sm font-semibold">
                      {aytProgram.completedTasks} / {aytProgram.totalTasks} Görev
                    </span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2.5">
                    <div
                      className="bg-white h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${aytProgram.completionPercentage || 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm mb-4">
                  <div className="flex items-center space-x-2">
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>%{Math.round(aytProgram.completionPercentage || 0)} Tamamlandı</span>
                  </div>
                  <span className="opacity-90">
                    {aytProgram.totalTasks - aytProgram.completedTasks} görev kaldı
                  </span>
                </div>

                <Link
                  to={`/study-program?type=AYT`}
                  className="block w-full px-4 py-2 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition text-center"
                >
                  Programa Git →
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Stats Header */}
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">İstatistikler</h2>

        {/* Stats Cards - Mobile Responsive */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 lg:p-6 border-l-4 border-primary-500">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Toplam Deneme</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.totalExams || 0}</span>
                </div>
              </div>
              <ChartBarIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-primary-500 self-end sm:self-auto" />
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 lg:p-6 border-l-4 border-success-500">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">Ortalama Net</p>
                {stats?.tyt && stats?.ayt ? (
                  <div className="flex items-baseline gap-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-blue-600 font-medium">TYT</span>
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {stats.tyt.averageNet || '0.00'}
                      </span>
                    </div>
                    <div className="w-px h-8 bg-gray-300"></div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-purple-600 font-medium">AYT</span>
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {stats.ayt.averageNet || '0.00'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats?.averageNet || '0.00'}</p>
                )}
              </div>
              <TrophyIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-success-500 self-end sm:self-auto" />
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 lg:p-6 border-l-4 border-warning-500">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1">En İyi Net</p>
                {stats?.tyt && stats?.ayt ? (
                  <div className="flex items-baseline gap-3">
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-blue-600 font-medium">TYT</span>
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {stats.tyt.bestExam?.totalNet || '0.00'}
                      </span>
                    </div>
                    <div className="w-px h-8 bg-gray-300"></div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-xs text-purple-600 font-medium">AYT</span>
                      <span className="text-2xl sm:text-3xl font-bold text-gray-900">
                        {stats.ayt.bestExam?.totalNet || '0.00'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {stats?.bestExam?.totalNet || '0.00'}
                  </p>
                )}
              </div>
              <ClockIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-warning-500 self-end sm:self-auto" />
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-3 sm:p-4 lg:p-6 border-l-4 border-error-500">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">Hedef Alan</p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{user?.targetField}</p>
              </div>
              <ChartBarIcon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-error-500 self-end sm:self-auto" />
            </div>
          </div>
        </div>

        {/* Quick Actions - Mobile Responsive */}
        <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
          <Link
            to="/add-exam"
            className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-primary-600 text-white rounded-lg shadow-lg hover:bg-primary-700 transition duration-150 font-semibold text-sm sm:text-base"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Yeni Deneme Ekle
          </Link>
          
          {/* Test Denemesi Oluştur Butonu - Sadece development'ta */}
          {process.env.NODE_ENV === 'development' && (
            <button
              onClick={handleCreateTestExam}
              disabled={creatingTest}
              className="inline-flex items-center justify-center px-4 sm:px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg shadow-lg hover:from-purple-700 hover:to-pink-700 transition duration-150 font-semibold disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {creatingTest ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Oluşturuluyor...
                </>
              ) : (
                <>
                  <span className="text-xl mr-2">🎲</span>
                  Test Denemeleri Oluştur (TYT+AYT)
                </>
              )}
            </button>
          )}
        </div>

        {/* Charts Section - Mobile Responsive */}
        {allExams.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
            {/* Net Progress Chart */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base sm:text-xl font-bold text-gray-900">📈 Net Gelişimi</h2>
                <span className="text-xs sm:text-sm text-gray-500">{allExams.length} Deneme</span>
              </div>
              <NetProgressChart exams={allExams} />
            </div>

            {/* Subject Trends with Checkboxes */}
            <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base sm:text-xl font-bold text-gray-900">📊 Ders Trendleri</h2>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={subjectViewType === 'TYT'}
                      onChange={() => setSubjectViewType('TYT')}
                      className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">TYT</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={subjectViewType === 'AYT'}
                      onChange={() => setSubjectViewType('AYT')}
                      className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">AYT</span>
                  </label>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {subjectViewType === 'TYT' ? (
                  <>
                    {/* TYT - Türkçe */}
                    <div className="bg-blue-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-blue-900">📖 Türkçe</span>
                        <span className="text-xs text-blue-600">
                          {stats?.subjectAverages?.tyt?.turkish || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'TYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.turkish || 0;
                          const maxNet = 40;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-blue-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* TYT - Matematik */}
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-green-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-green-900">🔢 Mat</span>
                        <span className="text-xs text-green-600">
                          {stats?.subjectAverages?.tyt?.math || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'TYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.math || 0;
                          const maxNet = 40;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-green-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* TYT - Fen */}
                    <div className="bg-purple-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-purple-900">🔬 Fen</span>
                        <span className="text-xs text-purple-600">
                          {stats?.subjectAverages?.tyt?.science || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'TYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.science || 0;
                          const maxNet = 20;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-purple-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* TYT - Sosyal */}
                    <div className="bg-orange-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-orange-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-orange-900">🌍 Sosyal</span>
                        <span className="text-xs text-orange-600">
                          {stats?.subjectAverages?.tyt?.social || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'TYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.social || 0;
                          const maxNet = 20;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-orange-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* AYT - Matematik */}
                    <div className="bg-green-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-green-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-green-900">🔢 Mat</span>
                        <span className="text-xs text-green-600">
                          {stats?.subjectAverages?.ayt?.math || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'AYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.math || 0;
                          const maxNet = 40;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-green-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* AYT - Fizik */}
                    <div className="bg-purple-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-purple-900">⚛️ Fizik</span>
                        <span className="text-xs text-purple-600">
                          {stats?.subjectAverages?.ayt?.physics || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'AYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.physics || 0;
                          const maxNet = 14;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-purple-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* AYT - Kimya */}
                    <div className="bg-blue-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-blue-900">🧪 Kimya</span>
                        <span className="text-xs text-blue-600">
                          {stats?.subjectAverages?.ayt?.chemistry || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'AYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.chemistry || 0;
                          const maxNet = 13;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-blue-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* AYT - Biyoloji */}
                    <div className="bg-orange-50 rounded-lg p-2 sm:p-3 lg:p-4 border border-orange-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs sm:text-sm font-semibold text-orange-900">🧬 Biyoloji</span>
                        <span className="text-xs text-orange-600">
                          {stats?.subjectAverages?.ayt?.biology || '0.00'}
                        </span>
                      </div>
                      <div className="h-8 flex items-end gap-0.5">
                        {allExams.filter(e => e.examType === 'AYT').slice(-5).map((exam, idx) => {
                          const net = exam.nets?.biology || 0;
                          const maxNet = 13;
                          const height = (net / maxNet) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-orange-400 rounded-t"
                              style={{ height: `${height}%`, minHeight: '4px' }}
                              title={`${net.toFixed(1)} net`}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <p className="text-xs text-gray-500 mt-3 text-center">
                Son 5 {subjectViewType} denemenin trendi
              </p>
            </div>
          </div>
        )}

        {/* Recent Exams - Mobile Responsive */}
        <div className="bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-base sm:text-xl font-bold text-gray-900">Son Denemeler</h2>
            <Link to="/exams" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
              Tümünü Gör →
            </Link>
          </div>

          {recentExams.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <ChartBarIcon className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-base sm:text-lg mb-2">Henüz deneme eklemediniz</p>
              <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6">İlk denemenizi ekleyerek takibe başlayın</p>
              <Link
                to="/add-exam"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition duration-150 font-semibold text-sm sm:text-base"
              >
                <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                İlk Denemeyi Ekle
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tarih
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tür
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net
                    </th>
                    <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Puan
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentExams.map((exam) => (
                    <tr 
                      key={exam.id} 
                      onClick={() => navigate(`/exam/${exam.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition"
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {new Date(exam.examDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span className={`px-2 sm:px-3 py-0.5 sm:py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          exam.examType === 'TYT' 
                            ? 'bg-primary-100 text-primary-800' 
                            : 'bg-success-100 text-success-800'
                        }`}>
                          {exam.examType}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-semibold text-gray-900">
                        {exam.totalNet.toFixed(2)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900">
                        {exam.estimatedScore || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Subject Averages - Mobile Responsive */}
        {stats?.subjectAverages && Object.keys(stats.subjectAverages).length > 0 && (
          <div className="mt-6 sm:mt-8 bg-white rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
              <h2 className="text-base sm:text-xl font-bold text-gray-900">📊 Ders Trendleri</h2>
              <div className="flex gap-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={subjectViewType === 'TYT'}
                    onChange={(e) => setSubjectViewType(e.target.checked ? 'TYT' : 'AYT')}
                    className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">TYT</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={subjectViewType === 'AYT'}
                    onChange={(e) => setSubjectViewType(e.target.checked ? 'AYT' : 'TYT')}
                    className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">AYT</span>
                </label>
              </div>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              {subjectViewType === 'TYT' ? (
                <>
                  <div className="bg-red-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Türkçe</p>
                    <p className="text-xl sm:text-2xl font-bold text-red-600">
                      {stats.subjectAverages?.tyt?.turkish || '0.00'}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Matematik</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">
                      {stats.subjectAverages?.tyt?.math || '0.00'}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Fen</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600">
                      {stats.subjectAverages?.tyt?.science || '0.00'}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Sosyal</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-600">
                      {stats.subjectAverages?.tyt?.social || '0.00'}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Matematik</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">
                      {stats.subjectAverages?.ayt?.math || '0.00'}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Fizik</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600">
                      {stats.subjectAverages?.ayt?.physics || '0.00'}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Kimya</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">
                      {stats.subjectAverages?.ayt?.chemistry || '0.00'}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-3 sm:p-4">
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">Biyoloji</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-600">
                      {stats.subjectAverages?.ayt?.biology || '0.00'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
